import { apiClient } from "@/lib/api";

/** A single header/footer navigation link configured by a dealer. */
export interface BrandNavLink {
  label: string;
  url: string;
}

/** Brand colors, as `#RGB`/`#RRGGBB` hex strings (empty == use platform default). */
export interface BrandColors {
  primary: string;
  sidebar: string;
  accent: string;
}

/**
 * Response shape of `GET /api/public/v1/branding/` (design §4.1).
 *
 * Every string/array field is always present; empty values (`""` / `[]`) mean
 * "use the platform default". When `is_default` is `true` the whole payload is
 * the platform brand and the frontend must leave the CSS-var/title/favicon
 * defaults untouched.
 */
export interface Branding {
  brand_name: string;
  logo_url: string;
  logo_dark_url: string;
  favicon_url: string;
  colors: BrandColors;
  active_template: string;
  header_links: BrandNavLink[];
  footer_links: BrandNavLink[];
  support_url: string;
  is_default: boolean;
}

/**
 * Fetches the active brand for the web app.
 *
 * The endpoint is PUBLIC (`AllowAny`) and needs no JWT, but it is served via
 * `apiClient` per the `src/api/<domain>.ts` convention. The auth interceptor
 * attaching a Bearer token here is harmless — the backend ignores it for this
 * `AllowAny` view — and keeps the module consistent with the rest of the app.
 *
 * In Phase A the brand is resolved by `?dealer=<slug>` or falls back to the
 * platform default (`is_default: true`); host-based resolution arrives in
 * Phase B without any frontend change.
 *
 * GET /api/public/v1/branding/
 */
export async function getBranding(dealerSlug?: string): Promise<Branding> {
  const { data } = await apiClient.get<Branding>("/public/v1/branding/", {
    params: dealerSlug ? { dealer: dealerSlug } : undefined,
  });
  return data;
}

/**
 * Fetches the authenticated user's active brand — resolved from
 * `request.user.tenant.dealer.branding`. Falls back to `is_default: true`
 * when the user has no tenant/dealer/branding (e.g. dealer/superadmin users,
 * whose `tenant` is `null`), so switching an authenticated surface to this
 * source gives them the platform default for free.
 *
 * Same nested {@link Branding} shape as the public {@link getBranding}. The
 * axios baseURL is `/api`, so the path is `/v1/branding/`.
 *
 * GET /api/v1/branding/ (IsAuthenticated)
 */
export async function getMyBranding(): Promise<Branding> {
  const { data } = await apiClient.get<Branding>("/v1/branding/");
  return data;
}

/** Supported branding template presets (backend `DealerBranding.Template`). */
export type BrandTemplate = "classic" | "minimal" | "bold";

/**
 * Flat read/write shape of the authenticated dealer branding endpoint
 * (`GET/PUT /api/v1/dealer/branding/`). Unlike the public {@link Branding}
 * payload (which nests `colors`), the dealer endpoint exposes the raw model
 * fields (`primary_color` etc.) plus a read-only `dealer_slug`.
 */
export interface DealerBranding {
  brand_name: string;
  support_url: string;
  logo_url: string;
  logo_dark_url: string;
  favicon_url: string;
  email_logo_url: string;
  primary_color: string;
  sidebar_color: string;
  accent_color: string;
  email_from_name: string;
  active_template: BrandTemplate;
  header_links: BrandNavLink[];
  footer_links: BrandNavLink[];
  /** Read-only: the dealer's `?dealer=<slug>` preview value (may be null). */
  dealer_slug: string | null;
}

/** Writable subset of {@link DealerBranding} (everything except `dealer_slug`). */
export type DealerBrandingInput = Omit<DealerBranding, "dealer_slug">;

/**
 * Fetches the requesting dealer's own branding.
 * GET /api/v1/dealer/branding/ (JWT, role `dealer`)
 */
export async function getDealerBranding(): Promise<DealerBranding> {
  const { data } = await apiClient.get<DealerBranding>("/v1/dealer/branding/");
  return data;
}

/**
 * Upserts the requesting dealer's own branding. The backend always targets
 * `request.user`, so the payload can never address another dealer.
 * PUT /api/v1/dealer/branding/ (JWT, role `dealer`)
 */
export async function updateDealerBranding(
  payload: DealerBrandingInput,
): Promise<DealerBranding> {
  const { data } = await apiClient.put<DealerBranding>(
    "/v1/dealer/branding/",
    payload,
  );
  return data;
}

/**
 * Uploads a branding asset (logo / dark-logo / favicon / email-logo) through the
 * backend proxy. The file is POSTed as multipart/form-data to our own origin
 * (CORS under our control); the backend validates it (allowed image types, 5 MB
 * cap), streams it to S3-compatible storage with the correct public-read ACL and
 * content headers, and returns the public `file_url` to store on the matching
 * branding field.
 *
 * This replaces the old presigned-URL + direct browser→S3 PUT flow (BUG-376):
 * branded tenants use dynamic custom domains, so the S3 bucket CORS could not
 * list every browser origin and the direct-PUT preflight failed. Because the
 * upload no longer hits S3 directly, the previously signed `Content-Type` /
 * `Content-Disposition` / ACL headers (BUG-363) are set server-side and are no
 * longer part of the client contract.
 * POST /api/v1/dealer/branding/asset-upload/ (JWT, role `dealer`)
 */
export async function uploadBrandingAsset(
  file: File,
): Promise<{ file_url: string }> {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await apiClient.post<{ file_url: string }>(
    "/v1/dealer/branding/asset-upload/",
    formData,
  );
  return data;
}

/**
 * Rendered branded-email preview payload (`{html}`) returned by both the dealer
 * and the admin email-preview endpoints (White-Label Phase E, TASK-3803).
 */
export interface BrandingEmailPreview {
  html: string;
}

/**
 * Reads a specific dealer's branding — superadmin only (White-Label Phase A).
 * Same `DealerBranding` shape as the dealer endpoint (email fields included).
 * GET /api/v1/admin/dealers/{dealerId}/branding/ (JWT, role `superadmin`)
 */
export async function getAdminDealerBranding(
  dealerId: number,
): Promise<DealerBranding> {
  const { data } = await apiClient.get<DealerBranding>(
    `/v1/admin/dealers/${dealerId}/branding/`,
  );
  return data;
}

/**
 * Upserts a specific dealer's branding — superadmin only. A non-dealer / unknown
 * id returns 404; invalid input returns `400 {field: [...]}`.
 * PUT /api/v1/admin/dealers/{dealerId}/branding/ (JWT, role `superadmin`)
 */
export async function updateAdminDealerBranding(
  dealerId: number,
  payload: DealerBrandingInput,
): Promise<DealerBranding> {
  const { data } = await apiClient.put<DealerBranding>(
    `/v1/admin/dealers/${dealerId}/branding/`,
    payload,
  );
  return data;
}

/* ------------------------------------------------------------------ *
 * White-Label Phase F — dealer-editable email text (TASK-3850/3852)  *
 * ------------------------------------------------------------------ */

/**
 * The four STORED email-text kinds. `resend` is NOT a stored kind — it inherits
 * the `invitation` row at send time, so the UI maps `resend` → `invitation`
 * before calling the backend (see {@link storedEmailTextKind}).
 */
export type StoredEmailTextKind =
  | "notification"
  | "password_reset"
  | "invitation"
  | "route_completed";

/**
 * The kinds the editor's selector offers. Adds the synthetic `resend` entry that
 * shares the `invitation` row.
 */
export type EmailTextKind = StoredEmailTextKind | "resend";

/**
 * Resolves the selector kind to the STORED kind used by the backend: `resend`
 * collapses to `invitation`; every other kind maps to itself.
 */
export function storedEmailTextKind(kind: EmailTextKind): StoredEmailTextKind {
  return kind === "resend" ? "invitation" : kind;
}

/**
 * One dealer email-text override. The backend MATERIALIZES a blank-default row
 * ({@link subject}/{@link intro}/{@link footer_signature} all `""`) when nothing
 * is saved, so the client always receives a valid object; a blank field means
 * "use the platform default copy" for that template.
 */
export interface DealerEmailText {
  kind: StoredEmailTextKind;
  subject: string;
  intro: string;
  footer_signature: string;
}

/** Writable subset of {@link DealerEmailText} (the `kind` comes from the path). */
export type DealerEmailTextInput = Pick<
  DealerEmailText,
  "subject" | "intro" | "footer_signature"
>;

/** Draft text passed to the live-preview endpoint as query params. */
export type EmailTextDraft = DealerEmailTextInput;

/**
 * Per-kind email-text preview payload. With a `kind` the backend returns the
 * computed `subject` alongside the rendered `html`.
 */
export interface EmailTextPreview extends BrandingEmailPreview {
  subject?: string;
}

/**
 * Fetches the requesting dealer's saved (or materialized-blank) email text for a
 * single kind.
 * GET /api/v1/dealer/branding/email-texts/<kind>/ (JWT, role `dealer`)
 */
export async function getDealerEmailText(
  kind: StoredEmailTextKind,
): Promise<DealerEmailText> {
  const { data } = await apiClient.get<DealerEmailText>(
    `/v1/dealer/branding/email-texts/${kind}/`,
  );
  return data;
}

/**
 * Upserts the requesting dealer's email text for a single kind. The backend
 * always targets `request.user`, so the payload can never address another dealer.
 * PUT /api/v1/dealer/branding/email-texts/<kind>/ (JWT, role `dealer`)
 */
export async function updateDealerEmailText(
  kind: StoredEmailTextKind,
  payload: DealerEmailTextInput,
): Promise<DealerEmailText> {
  const { data } = await apiClient.put<DealerEmailText>(
    `/v1/dealer/branding/email-texts/${kind}/`,
    payload,
  );
  return data;
}

/**
 * Renders the requesting dealer's email preview for a kind using DRAFT text
 * (unsaved edits) — falling back to saved text, then platform defaults, per
 * missing field. The selector's synthetic `resend` is sent verbatim (the backend
 * maps it to the invitation template).
 * GET /api/v1/dealer/branding/email-preview/?kind=…&subject=…&intro=…&footer_signature=…
 */
export async function getDealerEmailTextPreview(
  kind: EmailTextKind,
  draft: EmailTextDraft,
): Promise<EmailTextPreview> {
  const { data } = await apiClient.get<EmailTextPreview>(
    "/v1/dealer/branding/email-preview/",
    { params: { kind, ...draft } },
  );
  return data;
}

/**
 * Sends a test email of the selected kind to the requesting dealer using their
 * SAVED text (never draft — keeps "sent == stored").
 * POST /api/v1/dealer/branding/send-test-email/ (JWT, role `dealer`)
 */
export async function sendDealerEmailTextTest(
  kind: EmailTextKind,
): Promise<{ detail: string }> {
  const { data } = await apiClient.post<{ detail: string }>(
    "/v1/dealer/branding/send-test-email/",
    { kind },
  );
  return data;
}

/**
 * Admin-on-behalf variant of {@link getDealerEmailText} — superadmin only.
 * GET /api/v1/admin/dealers/{dealerId}/branding/email-texts/<kind>/
 */
export async function getAdminDealerEmailText(
  dealerId: number,
  kind: StoredEmailTextKind,
): Promise<DealerEmailText> {
  const { data } = await apiClient.get<DealerEmailText>(
    `/v1/admin/dealers/${dealerId}/branding/email-texts/${kind}/`,
  );
  return data;
}

/**
 * Admin-on-behalf variant of {@link updateDealerEmailText} — superadmin only.
 * PUT /api/v1/admin/dealers/{dealerId}/branding/email-texts/<kind>/
 */
export async function updateAdminDealerEmailText(
  dealerId: number,
  kind: StoredEmailTextKind,
  payload: DealerEmailTextInput,
): Promise<DealerEmailText> {
  const { data } = await apiClient.put<DealerEmailText>(
    `/v1/admin/dealers/${dealerId}/branding/email-texts/${kind}/`,
    payload,
  );
  return data;
}

/**
 * Admin-on-behalf variant of {@link getDealerEmailTextPreview} — superadmin only.
 * GET /api/v1/admin/dealers/{dealerId}/branding/email-preview/?kind=…&…
 */
export async function getAdminDealerEmailTextPreview(
  dealerId: number,
  kind: EmailTextKind,
  draft: EmailTextDraft,
): Promise<EmailTextPreview> {
  const { data } = await apiClient.get<EmailTextPreview>(
    `/v1/admin/dealers/${dealerId}/branding/email-preview/`,
    { params: { kind, ...draft } },
  );
  return data;
}

/**
 * Admin-on-behalf variant of {@link sendDealerEmailTextTest} — superadmin only.
 * The test email lands in the superadmin's own inbox.
 * POST /api/v1/admin/dealers/{dealerId}/branding/send-test-email/
 */
export async function sendAdminDealerEmailTextTest(
  dealerId: number,
  kind: EmailTextKind,
): Promise<{ detail: string }> {
  const { data } = await apiClient.post<{ detail: string }>(
    `/v1/admin/dealers/${dealerId}/branding/send-test-email/`,
    { kind },
  );
  return data;
}
